/**
 * The error taxonomy for the entire system.
 *
 * Rules:
 *  - Every error crossing a boundary is an `AppError` with a stable machine
 *    code. Clients branch on `code`, never on message text.
 *  - `expose` decides whether the message reaches the client. Anything derived
 *    from an upstream failure is redacted so we can never leak a connection
 *    string or a vendor's error payload into a browser.
 *  - Codes are append-only. Renaming one is a breaking API change.
 */
export const ERROR_CODES = {
    /** Request failed schema validation. `details.issues` describes each field. */
    VALIDATION_FAILED: 'VALIDATION_FAILED',
    /** No valid credentials were presented. */
    UNAUTHENTICATED: 'UNAUTHENTICATED',
    /** Authenticated, but not permitted to perform this action. */
    FORBIDDEN: 'FORBIDDEN',
    /** The addressed resource does not exist, or is not visible to the caller. */
    NOT_FOUND: 'NOT_FOUND',
    /** The request conflicts with current state (duplicate, version mismatch). */
    CONFLICT: 'CONFLICT',
    /** The caller exceeded a rate limit. */
    RATE_LIMITED: 'RATE_LIMITED',
    /** A downstream dependency failed (database, storage, third-party API). */
    DEPENDENCY_FAILURE: 'DEPENDENCY_FAILURE',
    /** Anything unhandled. Always redacted before it leaves the process. */
    INTERNAL: 'INTERNAL',
};
export class AppError extends Error {
    details;
    constructor(message, options = {}) {
        super(message, options.cause === undefined ? undefined : { cause: options.cause });
        this.name = new.target.name;
        this.details = options.details;
        if (typeof Error.captureStackTrace === 'function') {
            Error.captureStackTrace(this, new.target);
        }
    }
}
export class ValidationError extends AppError {
    code = ERROR_CODES.VALIDATION_FAILED;
    httpStatus = 400;
    expose = true;
    constructor(message = 'The request payload is invalid.', options = {}) {
        super(message, options);
    }
}
export class UnauthenticatedError extends AppError {
    code = ERROR_CODES.UNAUTHENTICATED;
    httpStatus = 401;
    expose = true;
    constructor(message = 'Authentication is required.', options = {}) {
        super(message, options);
    }
}
export class ForbiddenError extends AppError {
    code = ERROR_CODES.FORBIDDEN;
    httpStatus = 403;
    expose = true;
    constructor(message = 'You do not have access to this resource.', options = {}) {
        super(message, options);
    }
}
export class NotFoundError extends AppError {
    code = ERROR_CODES.NOT_FOUND;
    httpStatus = 404;
    expose = true;
    constructor(message = 'The requested resource was not found.', options = {}) {
        super(message, options);
    }
}
export class ConflictError extends AppError {
    code = ERROR_CODES.CONFLICT;
    httpStatus = 409;
    expose = true;
    constructor(message = 'The request conflicts with the current state of the resource.', options = {}) {
        super(message, options);
    }
}
export class RateLimitedError extends AppError {
    code = ERROR_CODES.RATE_LIMITED;
    httpStatus = 429;
    expose = true;
    constructor(message = 'Too many requests. Please retry shortly.', options = {}) {
        super(message, options);
    }
}
/**
 * A dependency we do not control failed. The caller gets a generic message; the
 * underlying cause is logged server-side with the request id for correlation.
 */
export class DependencyFailureError extends AppError {
    code = ERROR_CODES.DEPENDENCY_FAILURE;
    httpStatus = 502;
    expose = false;
    constructor(message, options = {}) {
        super(message, options);
    }
}
export class InternalError extends AppError {
    code = ERROR_CODES.INTERNAL;
    httpStatus = 500;
    expose = false;
    constructor(message = 'An unexpected error occurred.', options = {}) {
        super(message, options);
    }
}
export function isAppError(value) {
    return value instanceof AppError;
}
/**
 * Normalises anything thrown into an `AppError`.
 *
 * Every catch block at a boundary funnels through this so that no unknown shape
 * ever reaches the response serialiser.
 */
export function toAppError(value) {
    if (isAppError(value))
        return value;
    if (value instanceof Error) {
        return new InternalError(value.message, { cause: value });
    }
    return new InternalError('An unexpected non-error value was thrown.', {
        details: { thrown: typeof value },
        cause: value,
    });
}
//# sourceMappingURL=errors.js.map